import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { GLOBAL } from 'src/app/_config/config';
import { MembratedLetterByBatch, MembratedLetterFile, TonnageAvailableByBatch } from 'src/app/interfaces/membratedLetter.interfaces';

type Module = 'E' | 'C';

/**
 * Servicio que gestiona las cartas membretadas de los lotes de exportación y
 * cabotaje, permitiendo listarlas, consultar el tonelaje disponible, obtener y
 * crear sus archivos, eliminarlas y dar formato a sus datos de fecha y usuario.
 */
@Injectable({
  providedIn: 'root'
})
export class MembratedLetterService {
  areThereMembratedLetters: boolean = false;

  constructor(
    private http: HttpClient
  ) { }

  /**
   * Obtiene las cartas membretadas de un lote, según el módulo, con la fecha y
   * el usuario formateados.
   * @param module - Módulo de origen: 'E' exportación o 'C' cabotaje.
   * @param batchId - Identificador del lote.
   * @returns Observable con la lista de cartas membretadas del lote.
   */
  getMembratedLetterListByBatch(module: Module, batchId: number): Observable<MembratedLetterByBatch[]> {
    if (module === 'E') {
      const url: string = `${GLOBAL.url}${GLOBAL.loteExportacion.entradas.membratedLetter.listByBatch}${batchId}`;
      return this.http.get<MembratedLetterByBatch[]>(url).pipe(
        map( membratedLetterListByBatch => {
          return membratedLetterListByBatch.map( letter => ({
            ...letter,
            date: this.formattedDate(letter.date),
            user: this.formattedText(letter.user)
          }));
        })
      );
    } else {
      const url: string = `${GLOBAL.url}${GLOBAL.loteCabotaje.entradas.membratedLetter.listByBatch}${batchId}`;
      return this.http.get<MembratedLetterByBatch[]>(url).pipe(
        map( membratedLetterListByBatch => {
          return membratedLetterListByBatch.map( letter => ({
            ...letter,
            date: this.formattedDate(letter.date),
            user: this.formattedText(letter.user)
          }));
        })
      );
    }
  }

  /**
   * Obtiene el tonelaje disponible de un lote según el módulo.
   * @param module - Módulo de origen: 'E' exportación o 'C' cabotaje.
   * @param batchId - Identificador del lote.
   * @returns Observable con el tonelaje disponible del lote.
   */
  getTonnageAvailableByBatch(module: Module, batchId: number): Observable<TonnageAvailableByBatch[]> {
    if (module === 'E') {
      const url: string = `${GLOBAL.url}${GLOBAL.loteExportacion.entradas.membratedLetter.tonnageAvailableByBatch}${batchId}`;
      return this.http.get<TonnageAvailableByBatch[]>(url);
    } else {
      const url: string = `${GLOBAL.url}${GLOBAL.loteCabotaje.entradas.membratedLetter.tonnageAvailableByBatch}${batchId}`;
      return this.http.get<TonnageAvailableByBatch[]>(url);
    }
  }

  /**
   * Obtiene el archivo de una carta membretada según el módulo.
   * @param module - Módulo de origen: 'E' exportación o 'C' cabotaje.
   * @param batchId - Identificador del lote.
   * @param letterId - Identificador del documento de la carta.
   * @returns Observable con el archivo de la carta membretada.
   */
  getMembratedLetter(module: Module, batchId: number, letterId: number): Observable<MembratedLetterFile> {
    if (module === 'E') {
      const url: string = `${GLOBAL.url}${GLOBAL.loteExportacion.entradas.membratedLetter.get}`;
      return this.http.post<MembratedLetterFile>(url, {
        id_export_batch: batchId,
        document_id: letterId
      });
    } else {
      const url: string = `${GLOBAL.url}${GLOBAL.loteCabotaje.entradas.membratedLetter.get}`;
      return this.http.post<MembratedLetterFile>(url, {
        cabotage_batch_id: batchId,
        document_id: letterId
      });
    }
  }

  /**
   * Crea una carta membretada para un lote según el módulo.
   * @param module - Módulo de origen: 'E' exportación o 'C' cabotaje.
   * @param batchId - Identificador del lote.
   * @param weight - Peso (tonelaje) asociado a la carta.
   * @param customs_agency - Agencia aduanal asociada a la carta.
   * @returns Observable con el archivo de la carta membretada creada.
   */
  createMembratedLetter(module: Module, batchId: number, weight: number, customs_agency: string): Observable<MembratedLetterFile> {
    if (module === 'E') {
      const url: string = `${GLOBAL.url}${GLOBAL.loteExportacion.entradas.membratedLetter.create}`;
      return this.http.post<MembratedLetterFile>(url, {
        customs_agency,
        weight,
        id_export_batch: batchId,
      });
    } else {
      const url: string = `${GLOBAL.url}${GLOBAL.loteCabotaje.entradas.membratedLetter.create}`;
      return this.http.post<MembratedLetterFile>(url, {
        customs_agency,
        weight,
        cabotage_batch_id: batchId,
      });
    }
  }

  /**
   * Elimina una carta membretada según el módulo.
   * @param module - Módulo de origen: 'E' exportación o 'C' cabotaje.
   * @param letterId - Identificador de la carta a eliminar.
   * @returns Observable con la respuesta de la eliminación.
   */
  deleteMembratedLetter(module: Module, letterId: number): Observable<string> {
    if (module === 'E') {
      const url: string = `${GLOBAL.url}${GLOBAL.loteExportacion.entradas.membratedLetter.delete}${letterId}`;
      return this.http.delete<string>(url);
    } else {
      const url: string = `${GLOBAL.url}${GLOBAL.loteCabotaje.entradas.membratedLetter.delete}${letterId}`;
      return this.http.delete<string>(url);
    }
  }

  /**
   * Reordena una fecha del formato `dd-mm-aaaa` al formato `aaaa-mm-dd`.
   * @param date - Fecha en formato `dd-mm-aaaa`.
   * @returns Fecha reordenada en formato `aaaa-mm-dd`.
   */
  formattedDate(date: string): string {
    const parts = date.split('-');
    const day = parts[0];
    const month = parts[1];
    const year = parts[2];

    const newDate = `${year}-${month}-${day}`;
    return newDate;
  }

  /**
   * Obtiene el nombre del usuario a partir de su correo, capitalizando la inicial.
   * @param user - Correo electrónico del usuario.
   * @returns Nombre capitalizado del usuario.
   */
  formattedText(user: string): string {
    const name = user.split('@')[0];
    return name.charAt(0).toUpperCase() + name.slice(1);
  }
}
